<template>
  <Dialog
    v-model:visible="visibleBuilderCreateModal"
    :header="`${activeBusiness?.name}`"
    modal
    :style="{ width: '35rem' }"
  >
    <!-- Create an appraisal -->
    <div v-if="activeBuilderType === 'APPRAISAL'">
      <div
        v-if="isCheckingAppraisalExists"
        class="flex items-center gap-4 mb-4 flex-col"
      >
        <div class="flex items-center gap-4 mb-4">
          <ProgressSpinner />
        </div>
      </div>
      <div v-else-if="appraisalExists">
        <p>An appraisal already exists for this business.</p>
      </div>
      <div v-else>
        <p class="mb-3 ">
          An appraisal will be generated using your workspace's appraisal templates, including introduction, next steps,
          fee
          structure and other key sections.
        </p>
        <p class="text-sm text-gray-600">
          All sections can be fully customized after generation to match your specific needs. Ready to create your
          appraisal?
        </p>
      </div>
    </div>
    <div
      v-else
      class="grid grid-cols-1 items-center gap-4 mb-4"
    >
      <div v-if="isLoadingTemplates">
        <span class="w-full h-fit flex flex-col items-center justify-center gap-2">
          <ProgressSpinner style="width: 24px; height: 24px" />
          Loading templates...
        </span>
      </div>
      <template v-else>
        <div>Ready to create a new {{ activeBuilderType }}? Pick a template to begin.</div>
        <div v-if="activeBuilderType == 'CIM'">
          <div
            v-if="cimTemplates && cimTemplates.length >0"
            class="flex items-center gap-4 mb-4"
          >
            <p>Template:</p>
            <Select
              v-model="activeTemplate"
              :options="cimTemplates"
              optionLabel="title"
              optionValue="id"
              placeholder="Choose a template"
            />
          </div>
          <div v-else>
            This confirmation will only appear once.
          </div>
        </div>
        <div v-else-if="activeBuilderType == 'TEASER'">
          <div
            v-if="teaserTemplates && teaserTemplates.length >0"
            class="flex items-center gap-4 mb-4"
          >
            <p>Template:</p>
            <Select
              v-model="activeTemplate"
              :options="teaserTemplates"
              optionLabel="title"
              optionValue="id"
              placeholder="Choose a template"
            />
          </div>
          <div v-else>
            This confirmation will only appear once.
          </div>
        </div>
        <div v-else-if="activeBuilderType == 'PROPOSAL'">
          <div
            v-if="proposalTemplates && proposalTemplates.length >0"
            class="flex items-center gap-4 mb-4"
          >
            <p>Template:</p>
            <Select
              v-model="activeTemplate"
              :options="proposalTemplates"
              optionLabel="title"
              optionValue="id"
              placeholder="Choose a template"
            />
          </div>
          <div v-else>
            This confirmation will only appear once.
          </div>
        </div>
      </template>
    </div>
    <div class="flex justify-end gap-2">
      <Button
        type="button"
        label="Cancel"
        severity="secondary"
        @click="visibleBuilderCreateModal = false"
      />
      <Button
        type="button"
        label="Confirm"
        :loading="isGeneratingBuilder"
        @click="isThisAppraisalCreation ? generateAppraisal() : generateBuilder()"
      />
    </div>
  </Dialog>
</template>

<script setup lang="ts">
// Import
const { getTemplates, getTemplateById } = useTemplates()
const { saveBuilder } = useCim()
const { getAllBuilders, createBuilder } = useBuilder()
const { generateAppraisalFromTemplate } = useAppraisal()

// Set variables
const activeTemplate = ref()
const cimTemplates = ref([])
const proposalTemplates = ref([])
const isGeneratingBuilder = ref(false)
const teaserTemplates = ref([])
const isLoading = defineModel('isLoading', Boolean)
const isLoadingTemplates = ref(false)
const visibleBuilderCreateModal = defineModel('visibleBuilderCreateModal', Boolean)
const activeBusiness = defineModel('activeBusiness', Object)
const activeBuilderType = defineModel('activeBuilderType', String)
const isThisAppraisalCreation = defineModel('isThisAppraisalCreation', Boolean)
const router = useRouter()
const emit = defineEmits(['init'])

const { checkAppraisalExists } = useAppraisal()
const isCheckingAppraisalExists = ref(false)
const appraisalExists = ref(false)
const getAppraisalExists = async () => {
  try {
    console.log('🚀~activeBusiness.value:', activeBusiness.value)
    isCheckingAppraisalExists.value = true
    const response = await checkAppraisalExists(activeBusiness.value.id)
    console.log('🚀~response:', response)
    isCheckingAppraisalExists.value = false
    appraisalExists.value = response.data.exists
    if (appraisalExists.value) {
      console.log('🚀~appraisalExists.value:', appraisalExists.value)
      router.push(`/businesses/${activeBusiness?.value?.id}/appraisal`)
    }
  }
  catch (e) {
    console.error(e)
  }
}
// Load Data
const init = async () => {
  console.log('🚀~activeBuilderType.value:', activeBuilderType.value)
  if (activeBuilderType.value === 'APPRAISAL') {
    await getAppraisalExists()
  }
  isLoadingTemplates.value = true

  const cimTempData = await getTemplates('CIM')
  cimTemplates.value = cimTempData?.data

  const proposalTempData = await getTemplates('PROPOSAL')
  proposalTemplates.value = proposalTempData?.data

  const teaserTempData = await getTemplates('TEASER')
  teaserTemplates.value = teaserTempData?.data
  console.log('proposalTempData:', proposalTempData)
  isLoadingTemplates.value = false
}
onMounted(async () => {
  console.log('🚀~onMounted')
  await init()
})

// User Actions
const toast = useToast()
const generateBuilder = async () => {
  try {
    isGeneratingBuilder.value = true

    let sections = []
    if (activeTemplate.value) {
      // if template is selected then add template to the builder
      const template = await getTemplateById(activeTemplate.value)
      sections = template?.data?.Sections

      // console.log('🚀~sections:', sections)
      // console.log('🚀~template:', template)
      // await saveBuilder(activeBusiness.value?.id, sections, builderRes.data)
    }

    const builderRes = await createBuilder({
      title: activeBusiness?.value?.name,
      businessId: activeBusiness?.value?.id,
      workSpaceId: activeBusiness?.value?.workSpaceId,
      builderType: activeBuilderType.value,
      sections: sections,
    })
    // Show a big preloader in the main business page before redirecting
    isLoading.value = true

    // Redirect to the builder page
    router.push(`/businesses/${activeBusiness?.value?.id}/${activeBuilderType.value.toLowerCase()}`)

    isGeneratingBuilder.value = false
    visibleBuilderCreateModal.value = false
  }
  catch (e) {
    console.error(e)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `Failed to create a ${activeBuilderType.value}`,
      life: 3000,
    })
  }
}

const generateAppraisal = async () => {
  isGeneratingBuilder.value = true
  const response = await generateAppraisalFromTemplate(activeBusiness.value.id)
  isGeneratingBuilder.value = false
  visibleBuilderCreateModal.value = false

  router.push(`/businesses/${activeBusiness?.value?.id}/appraisal`)
}
</script>
